import { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { EyeIcon, EyeSlashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/authContext';
import { userService } from '../../adapters/user';

function validatePassword(value: string): string | undefined {
  if (value.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(value)) return 'Needs an uppercase letter';
  if (!/[a-z]/.test(value)) return 'Needs a lowercase letter';
  if (!/[0-9]/.test(value)) return 'Needs a number';
}

export function GeneralSettingsTab() {
  const { userData } = useAuth();
  const [editing, setEditing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const dirtyRef = useRef(false);

  const profileForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: userData?.firstname ?? '',
      lastName: userData?.lastname ?? '',
      email: userData?.email ?? '',
      mobile: String(userData?.mobile ?? ''),
      country: userData?.country ?? '',
      state: userData?.state ?? '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.firstName) errors.firstName = 'Required';
      if (!values.lastName) errors.lastName = 'Required';
      if (!values.email) errors.email = 'Required';
      if (!values.mobile) errors.mobile = 'Required';
      if (!values.country) errors.country = 'Required';
      if (!values.state) errors.state = 'Required';
      return errors;
    },
    onSubmit: async (values) => {
      setSavingProfile(true);
      try {
        await userService.editProfile({ ...values, password: '' });
        toast.success('Profile updated');
        setEditing(false);
        dirtyRef.current = false;
        profileForm.resetForm({ values });
      } catch {
        toast.error('Failed to update profile');
      } finally {
        setSavingProfile(false);
      }
    },
  });

  useEffect(() => {
    dirtyRef.current = editing && profileForm.dirty;
  }, [editing, profileForm.dirty]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const passwordForm = useFormik({
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.currentPassword) errors.currentPassword = 'Required';
      if (!values.newPassword) errors.newPassword = 'Required';
      const pwdError = validatePassword(values.newPassword);
      if (pwdError) errors.newPassword = pwdError;
      if (values.confirmPassword !== values.newPassword) errors.confirmPassword = 'Passwords do not match';
      return errors;
    },
    onSubmit: async (values) => {
      setChangingPassword(true);
      try {
        await userService.changePassword(values.currentPassword, values.newPassword);
        toast.success('Password changed');
        passwordForm.resetForm();
        setPwdOpen(false);
      } catch (e: any) {
        toast.error(e?.response?.data?.msg || 'Failed to change password');
      } finally {
        setChangingPassword(false);
      }
    },
  });

  const fullName = `${userData?.firstname ?? ''} ${userData?.lastname ?? ''}`.trim() || '—';

  return (
    <div className="space-y-8">
      {/* Profile Information */}
      <section className="bg-white border border-brand-navy/10 rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-brand-navy flex items-center gap-2">
              Profile Information
              {editing && <span className="text-xs font-medium text-amber-600">(Editing)</span>}
            </h2>
            {!editing && <p className="text-xs text-brand-navy/60 mt-1">Your personal details</p>}
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-brand-navy text-white hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-accent"
            >
              <PencilSquareIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>

        {!editing && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {[
              { label: 'Full Name', value: fullName },
              { label: 'Email', value: userData?.email || '—' },
              { label: 'Phone', value: String(userData?.mobile || '—') },
              { label: 'Country', value: userData?.country || '—' },
              { label: 'State', value: userData?.state || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <dt className="text-xs font-medium text-brand-navy/60 uppercase tracking-wide">{label}</dt>
                <dd className="mt-1 text-sm text-brand-navy">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {editing && (
          <form onSubmit={profileForm.handleSubmit} className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'firstName', label: 'First Name' },
                { name: 'lastName', label: 'Last Name' },
                { name: 'mobile', label: 'Phone' },
                { name: 'country', label: 'Country' },
                { name: 'state', label: 'State' },
              ].map(({ name, label }) => (
                <div key={name}>
                  <label htmlFor={`profile-${name}`} className="block text-xs font-medium text-brand-navy/80">{label}</label>
                  <input
                    id={`profile-${name}`}
                    name={name}
                    value={(profileForm.values as any)[name]}
                    onChange={profileForm.handleChange}
                    onBlur={profileForm.handleBlur}
                    className="mt-1 w-full rounded border border-brand-navy/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent"
                  />
                  {(profileForm.touched as any)[name] && (profileForm.errors as any)[name] && (
                    <p className="text-xs text-red-600 mt-1">{(profileForm.errors as any)[name]}</p>
                  )}
                </div>
              ))}
              <div>
                <label htmlFor="profile-email" className="block text-xs font-medium text-brand-navy/80">Email</label>
                <input
                  id="profile-email"
                  value={userData?.email ?? ''}
                  disabled
                  className="mt-1 w-full rounded border border-brand-navy/10 bg-gray-50 px-3 py-2 text-sm text-brand-navy/40"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { profileForm.resetForm(); setEditing(false); }}
                className="px-4 py-2 text-sm rounded border border-brand-navy/30 text-brand-navy bg-white hover:bg-brand-navy/5"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingProfile || !profileForm.dirty}
                className="px-4 py-2 text-sm rounded bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-accent"
              >
                {savingProfile ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* Password */}
      <section className="bg-white border border-brand-navy/10 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-brand-navy">Password</h2>
            <p className="text-xs text-brand-navy/60 mt-1">Use a strong password and change it regularly.</p>
          </div>
          <button
            onClick={() => setPwdOpen(true)}
            className="px-3 py-1.5 text-sm rounded bg-brand-navy text-white hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-accent"
          >
            Change
          </button>
        </div>
      </section>

      {/* Change Password Modal */}
      {pwdOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" aria-labelledby="pwd-modal-title">
          <div className="bg-white w-full max-w-md rounded-lg shadow-xl border border-brand-navy/10">
            <div className="px-5 py-4 border-b border-brand-navy/10 flex items-center justify-between">
              <h3 id="pwd-modal-title" className="text-sm font-semibold text-brand-navy">Change Password</h3>
              <button
                onClick={() => { setPwdOpen(false); passwordForm.resetForm(); }}
                className="text-brand-navy/40 hover:text-brand-navy/70 focus:outline-none focus:ring-2 focus:ring-brand-accent rounded"
                aria-label="Close password modal"
              >
                ✕
              </button>
            </div>
            <form onSubmit={passwordForm.handleSubmit} className="px-5 py-5 space-y-5">
              <div>
                <label htmlFor="pwd-current" className="block text-xs font-medium text-brand-navy/80">Current Password</label>
                <input
                  id="pwd-current"
                  type="password"
                  name="currentPassword"
                  value={passwordForm.values.currentPassword}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  className="mt-1 w-full rounded border border-brand-navy/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                />
                {passwordForm.touched.currentPassword && passwordForm.errors.currentPassword && (
                  <p className="text-xs text-red-600 mt-1">{passwordForm.errors.currentPassword}</p>
                )}
              </div>
              <div>
                <label htmlFor="pwd-new" className="block text-xs font-medium text-brand-navy/80">New Password</label>
                <div className="relative">
                  <input
                    id="pwd-new"
                    type={showNewPwd ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordForm.values.newPassword}
                    onChange={passwordForm.handleChange}
                    onBlur={passwordForm.handleBlur}
                    className="mt-1 w-full rounded border border-brand-navy/20 px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPwd((v) => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-brand-navy/50"
                    aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                  >
                    {showNewPwd ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {passwordForm.touched.newPassword && passwordForm.errors.newPassword && (
                  <p className="text-xs text-red-600 mt-1">{passwordForm.errors.newPassword}</p>
                )}
              </div>
              <div>
                <label htmlFor="pwd-confirm" className="block text-xs font-medium text-brand-navy/80">Confirm Password</label>
                <input
                  id="pwd-confirm"
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.values.confirmPassword}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  className="mt-1 w-full rounded border border-brand-navy/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                />
                {passwordForm.touched.confirmPassword && passwordForm.errors.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1">{passwordForm.errors.confirmPassword}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setPwdOpen(false); passwordForm.resetForm(); }}
                  className="px-4 py-2 text-sm rounded border border-brand-navy/30 text-brand-navy bg-white hover:bg-brand-navy/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-4 py-2 text-sm rounded bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-accent"
                >
                  {changingPassword ? 'Changing…' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
